import React, { useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaCalendarAlt,
  FaClock,
  FaDownload,
  FaFileAlt,
  FaGlobeAmericas,
  FaShieldAlt,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import "../../assets/styles/cms_preview_modal.scss";

const formatEffectiveDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

const formatUpdatedAt = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return value;
  }
};

export default function CmsPreviewModal({
  open,
  onClose,
  variant = "cms",
  title,
  subtitle,
  description,
  contentTitle,
  contentIntro,
  content,
  effectiveDate,
  updatedAt,
  updatedBy,
  publicUrl,
}) {
  const contentRef = useRef(null);

  const handleDownloadPdf = useCallback(() => {
    const html = contentRef.current?.innerHTML || content || "";
    const printWindow = window.open("", "_blank", "noopener,noreferrer");
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title || "Preview"}</title>
          <style>
            body { font-family: Inter, Arial, sans-serif; padding: 32px; color: #1e293b; line-height: 1.6; }
            h1, h2, h3 { color: #0f172a; }
          </style>
        </head>
        <body>${html}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }, [content, title]);

  if (!open || typeof document === "undefined") return null;

  const isRichVariant = variant === "terms" || variant === "page";
  const resolvedSubtitle =
    subtitle !== undefined && subtitle !== null
      ? subtitle
      : isRichVariant
        ? ""
        : description || "";

  return createPortal(
    <div className="cms-preview-modal" role="dialog" aria-modal="true" aria-labelledby="cms-preview-title">
      <div className="cms-preview-modal__backdrop" onClick={onClose} aria-hidden />
      <div className={`cms-preview-modal__panel${isRichVariant ? " cms-preview-modal__panel--terms" : ""}`}>
        <div className="cms-preview-modal__header">
          <div className="cms-preview-modal__header-main">
            {isRichVariant ? (
              <div className="cms-preview-modal__header-icon" aria-hidden>
                <FaFileAlt className="cms-preview-modal__header-doc" />
                <FaShieldAlt className="cms-preview-modal__header-shield" />
              </div>
            ) : null}
            <div className="cms-preview-modal__header-copy">
              {isRichVariant ? (
                <span className="cms-preview-modal__badge">Preview</span>
              ) : null}
              <h2 id="cms-preview-title">{title || "Preview"}</h2>
              {resolvedSubtitle ? (
                <p className="cms-preview-modal__subtitle">{resolvedSubtitle}</p>
              ) : null}
            </div>
          </div>
          <button type="button" className="cms-preview-modal__close" onClick={onClose} aria-label="Close preview">
            <FaTimes aria-hidden />
          </button>
        </div>

        {isRichVariant ? (
          <div className="cms-preview-modal__meta-bar">
            <div className="cms-preview-modal__meta-item">
              <span className="cms-preview-modal__meta-icon" aria-hidden>
                <FaCalendarAlt />
              </span>
              <span className="cms-preview-modal__meta-copy">
                <span className="cms-preview-modal__meta-label">Effective Date</span>
                <strong>{formatEffectiveDate(effectiveDate)}</strong>
              </span>
            </div>
            <div className="cms-preview-modal__meta-item">
              <span className="cms-preview-modal__meta-icon" aria-hidden>
                <FaClock />
              </span>
              <span className="cms-preview-modal__meta-copy">
                <span className="cms-preview-modal__meta-label">Last Updated</span>
                <strong>{formatUpdatedAt(updatedAt)}</strong>
              </span>
            </div>
            <div className="cms-preview-modal__meta-item cms-preview-modal__meta-item--updated-by">
              <span className="cms-preview-modal__meta-icon" aria-hidden>
                <FaUser />
              </span>
              <span className="cms-preview-modal__meta-copy">
                <span className="cms-preview-modal__meta-label">Updated By</span>
                <strong title={updatedBy || "Super Admin"}>{updatedBy || "Super Admin"}</strong>
              </span>
            </div>
          </div>
        ) : null}

        <div className={`cms-preview-modal__body${isRichVariant ? " cms-preview-modal__body--terms" : ""}`}>
          {isRichVariant ? (
            <div className="cms-preview-modal__document">
              {contentTitle ? (
                <div className="cms-preview-modal__document-head">
                  <FaFileAlt className="cms-preview-modal__document-icon" aria-hidden />
                  <h3>{contentTitle}</h3>
                </div>
              ) : null}
              {contentIntro ? <p className="cms-preview-modal__document-intro">{contentIntro}</p> : null}
              <div
                ref={contentRef}
                className="cms-preview-modal__document-content cms-rendered-html"
                dangerouslySetInnerHTML={{ __html: content || "<p>No content to preview.</p>" }}
              />
            </div>
          ) : (
            <>
              {description ? <p className="cms-preview-modal__description">{description}</p> : null}
              <div
                ref={contentRef}
                className="cms-preview-modal__content cms-rendered-html"
                dangerouslySetInnerHTML={{ __html: content || "<p>No content to preview.</p>" }}
              />
            </>
          )}
        </div>

        {isRichVariant ? (
          <div className="cms-preview-modal__footer">
            <button type="button" className="cms-preview-modal__btn cms-preview-modal__btn--outline" onClick={handleDownloadPdf}>
              <FaDownload aria-hidden />
              Download PDF
            </button>
            {publicUrl ? (
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cms-preview-modal__btn cms-preview-modal__btn--primary"
              >
                <FaGlobeAmericas aria-hidden />
                View Public Page
              </a>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
